/**
 * Live observed-orderings store — the feed seam for process-conformance.
 *
 * The server streams the distinct ordered service-hop sequences it observed
 * (model-free). A plugin mounts the provider and pushes them; a component inside
 * the model context (which knows each process's modeled step order) reads them
 * via {@link useLiveFlows} and compares — see {@link ./processConformance}.
 * Absent provider → NOOP → empty → no reorder highlighting.
 */
import { createContext, useContext, useSyncExternalStore } from "react";
import type { ObservedOrdering } from "./processConformance.js";

export interface LiveFlowsStore {
  subscribe(onChange: () => void): () => void;
  getFlows(): readonly ObservedOrdering[];
  /** Replace the observed orderings (empty clears). */
  setFlows(flows: readonly ObservedOrdering[]): void;
}

const EMPTY: readonly ObservedOrdering[] = [];

export function createLiveFlowsStore(): LiveFlowsStore {
  let flows: readonly ObservedOrdering[] = EMPTY;
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getFlows() {
      return flows;
    },
    setFlows(next) {
      if (next.length === 0) {
        if (flows.length === 0) return;
        flows = EMPTY;
      } else {
        flows = next;
      }
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<LiveFlowsStore | null>(null);
export const LiveFlowsProvider = Ctx.Provider;

const NOOP: LiveFlowsStore = {
  subscribe: () => () => {},
  getFlows: () => EMPTY,
  setFlows: () => {},
};

export function useLiveFlowsStore(): LiveFlowsStore | null {
  return useContext(Ctx);
}

/** The observed orderings from the mounted provider (empty when none). */
export function useLiveFlows(): readonly ObservedOrdering[] {
  const store = useContext(Ctx) ?? NOOP;
  return useSyncExternalStore(store.subscribe, store.getFlows, store.getFlows);
}
